import CheckCircle from '@mdi/svg/svg/check-circle.svg?react';
import ChevronDown from '@mdi/svg/svg/chevron-down.svg?react';
import Close from '@mdi/svg/svg/close.svg?react';
import InfoIcon from '@mdi/svg/svg/information-outline.svg?react';
import { type UserSession, useUserSession } from '@solinkcorp/authentication';
import { type LocaleDefinition } from '@solinkcorp/locale';
import {
  Button,
  ButtonMenu,
  Checkbox,
  Flex,
  MDIIcon,
  Pill,
  Radio,
  RadioGroup,
  TextArea,
  TextField,
  toast,
  Toggle,
} from '@solinkcorp/solink-components';
import { EVENT_TYPE_TRANSACTION } from '@solinkcorp/vd-common';
import classNames from 'classnames';
import { type IEvent, type Share } from 'clients/PlatformApi';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import React, { Component } from 'react';
import useLocale from 'src/hooks/useLocale';
import ComponentUtil from 'utils/ComponentUtil';
import * as EventUtil from 'utils/EventUtil';
import FeaturesUtil from 'utils/FeaturesUtil';
import LocaleUtil from 'utils/LocaleUtil';
import {
  CONTEXT_DOWNLOAD,
  EVENT_TYPES,
  type EventContextType,
  MAX_LENGTH_CLIP_TITLE,
  MAX_LENGTH_SHARE_MESSAGE,
  SHARE_STATUS_DISABLED,
  SHARE_STATUS_ENABLED,
  type ShareStatus,
  VISIBILITY_PRIVATE,
  VISIBILITY_PUBLIC,
  type VISIBILITY_TYPE,
} from 'vd-constants';
import { type CamerasVideoSourceMap, type SavePanelCamera } from '../types';
import { onlyOneVideoQuality } from '../utils/saveClipUtils';
import { getShareAccessStatusText } from '../utils/shareAccessStatusUtils';
import { getShareSummary, shouldOfferMotionOnlyVideo } from '../utils/shareSummaryUtils';
import './DetailsForm.sass';
import DetailsFormTimePicker from './DetailsFormTimePicker';
import ReceiptPopover from './ReceiptPopover';
import styles from './SaveDetails.styles';

interface DetailsFormProps {
  activeEvent?: IEvent;
  camerasVideoSourceMap: CamerasVideoSourceMap;
  canCreate: boolean;
  canShare: boolean;
  contextMode: EventContextType;
  description: string;
  diffShares: Partial<Share>[];
  displayFillGaps: boolean;
  emails: string;
  enableVisibilityEdit: boolean;
  endTime: Date | undefined;
  errorEmail: string;
  errorTime: string | undefined;
  errorTitle: string;
  event: IEvent | undefined;
  fillGaps: boolean;
  ifShare: boolean | undefined;
  isBestQuality: boolean;
  isCreator: boolean;
  isEditMode: boolean;
  saveMotionOnlyVideo: boolean;
  onBlur: (fieldName: string) => void;
  onChange: (fieldName: string, value: string | boolean | VISIBILITY_TYPE) => void;
  onDateChange: (fieldName: string, value: Date | undefined) => void;
  onFocusShareMessage: () => void;
  onKeyPressEmail: (e: React.KeyboardEvent) => void;
  removeShare: (share: Share) => void;
  saveExistingEvent: boolean;
  selectedCameras: { [key: string]: SavePanelCamera };
  shareMessage: string;
  shares: Share[];
  showCharCountShareMessage: boolean;
  startTime: Date | undefined;
  timezoneId: string;
  title: string | undefined;
  updateDiffSharesState: (diffShares: Partial<Share>[]) => void;
  updateEmailsState: (emails: string) => void;
  updateSaveActiveEvent: (value: boolean) => void;
  updateSharesState: (shares: Share[]) => void;
  updateShareMessageState: (message: string) => void;
  visibility: VISIBILITY_TYPE;
  faceblur: boolean;
  blurDurationInMs: number;
}

interface DetailsFormInnerProps extends DetailsFormProps {
  locale: LocaleDefinition;
  userSession: UserSession;
}

interface DetailsFormState {
  saveActiveEvent: boolean;
  unshareAll: boolean;
}

class DetailsForm extends Component<DetailsFormInnerProps, DetailsFormState> {
  private alreadyShared: boolean;

  static defaultProps = {
    canCreate: true,
    canShare: true,
    isCreator: false,
  };

  emailWrapperRef = React.createRef<HTMLDivElement>();
  emailInputRef = React.createRef<HTMLInputElement>();

  constructor(props: DetailsFormInnerProps) {
    super(props);

    const { shares } = props;

    this.alreadyShared = shares.length > 1;

    this.state = {
      saveActiveEvent: false,
      unshareAll: getShareSummary(shares).hasSharedAny,
    };
  }

  shouldComponentUpdate(nextProps: DetailsFormProps, nextState: DetailsFormState) {
    return ComponentUtil.shouldComponentUpdate({
      context: this,
      nextProps,
      nextState,
      deepCompareProps: ['diffShares', 'endTime', 'shares', 'startTime', 'selectedCameras'],
    });
  }

  componentDidUpdate(prevProps: DetailsFormProps) {
    const { diffShares, shares } = this.props;
    const { unshareAll } = this.state;
    const newState: Partial<DetailsFormState> = {};

    if (!isEqual(diffShares, prevProps.diffShares)) {
      const newUnshareAll = getShareSummary(shares).hasSharedAny;

      if (newUnshareAll !== unshareAll) {
        newState.unshareAll = newUnshareAll;
      }
    }

    if (Object.keys(newState).length) {
      this.setState(newState as DetailsFormState);
    }
  }

  render() {
    const {
      activeEvent,
      camerasVideoSourceMap,
      canCreate,
      canShare,
      contextMode,
      description,
      displayFillGaps,
      emails,
      enableVisibilityEdit,
      endTime,
      errorEmail,
      errorTitle,
      errorTime,
      event,
      fillGaps,
      isCreator,
      ifShare,
      isBestQuality,
      isEditMode,
      locale,
      saveMotionOnlyVideo,
      onBlur,
      onChange,
      onFocusShareMessage,
      onKeyPressEmail,
      removeShare,
      saveExistingEvent,
      selectedCameras,
      shareMessage,
      shares,
      showCharCountShareMessage,
      startTime,
      title,
      timezoneId,
      visibility,
      faceblur,
      blurDurationInMs,
      userSession,
    } = this.props;
    const { saveActiveEvent, unshareAll } = this.state;

    const shareOptions = shares.map((share) => ({
      ...share,
      status:
        !share.status || share.status === SHARE_STATUS_ENABLED
          ? SHARE_STATUS_ENABLED
          : (SHARE_STATUS_DISABLED as ShareStatus),
    }));

    const isEditableBookmarkEvent =
      event?.type === EVENT_TYPES.BOOKMARK && EventUtil.eventIsUserCreated(event);

    // `event` can be an empty object {}, hence the need to check for its key length.
    const displayEvent = event && Object.keys(event).length ? event : activeEvent;
    const { type: eventType, details, title: eventTitle } = displayEvent || {};
    const savingEvent = event || saveActiveEvent;
    const showDisplayOnlyTitle = savingEvent && saveExistingEvent && !isEditableBookmarkEvent;
    const displayOptionSavePlayingEvent = activeEvent;
    const isSharedEvent = event ? EventUtil.isShared(event) : false;
    const isOnlyOneVideoQuality = endTime && onlyOneVideoQuality(camerasVideoSourceMap, endTime);

    const displayFaceblurOption =
      FeaturesUtil.faceblurEnabled(userSession) && (!event || isEditableBookmarkEvent);

    const recipientsCount = getShareSummary(shares).recipientsCount;
    const highQualityToggleLabel = `${locale.cloudShare.highQuality}${
      isOnlyOneVideoQuality ? ` (${locale.cloudShare.onlyHaveOneQuality})` : ''
    }`;
    const saveClipQualityAndMotionOnlyDisabled = isOnlyOneVideoQuality || !canCreate;

    const displayMotionOnlyVideoCheckbox = shouldOfferMotionOnlyVideo({
      isSharedEvent,
      cameras: Object.values(selectedCameras),
      isBestQuality,
    });

    const unshareAllToggleText = unshareAll
      ? locale.cloudShare.recipientUnshareAll
      : locale.cloudShare.recipientShareAll;

    const timeFieldWrapperClass = classNames('text-field-wrapper', { error: errorTime });

    const videoSettingsWrapperClass = classNames('text-field-wrapper', 'video-settings-wrapper');

    const recipientWrapperClass = classNames('recipient-wrapper', { disabled: !ifShare });

    const shareMessageWrapperClass = classNames('share-message-wrapper', 'multiline-text', {
      'with-char': showCharCountShareMessage,
      disabled: !ifShare,
    });

    const emailWrapperClass = classNames('email-wrapper', { error: errorEmail && ifShare });

    const detailsFormTimePickerDisabled = !canCreate || (faceblur && !!event);

    return (
      <div className="details-form">
        <Flex flexDirection="column">
          <div className={timeFieldWrapperClass}>
            <div className="duration-instruction">{locale.cloudShare.durationInstruction}</div>
            <DetailsFormTimePicker
              timezoneId={timezoneId}
              startTime={startTime}
              endTime={endTime}
              disabled={detailsFormTimePickerDisabled}
              updateStartTime={this.updateStartTime}
              updateEndTime={this.updateEndTime}
              blurDurationInMs={blurDurationInMs}
              errorText={errorTime}
              faceblur={faceblur}
            />
          </div>
          {displayFillGaps &&
            (isEditMode ? (
              <div className="text-field-wrapper">
                <div className="details-title">{locale.cloudShare.downloadFillingGaps}</div>
                <div className="details-content capitalize">
                  {fillGaps ? locale.general.yes : locale.general.no}
                </div>
              </div>
            ) : (
              <div className="text-field-wrapper">
                <Checkbox
                  labelText={locale.cloudShare.downloadFillingGaps}
                  checked={fillGaps}
                  onChange={this.toggleFillGaps}
                  name="fill-gaps"
                />
              </div>
            ))}
          {displayOptionSavePlayingEvent && (
            <Flex className="save-event-checkbox" alignItems="center">
              <Checkbox
                labelText={locale.cloudShare.includeReceipt}
                checked={saveActiveEvent}
                onChange={this.checkSaveActiveEvent}
                name="save-current-event"
              />
              {!saveActiveEvent && <ReceiptPopover receipt={details?.Receipt} />}
            </Flex>
          )}
          {showDisplayOnlyTitle ? (
            <div className="details-item_wrapper">
              <div className="details-title existing-event-title">{locale.fields.title}</div>
              <div className={`details-content ${eventType}`}>
                <span className="text">{eventTitle}</span>
                {eventType === EVENT_TYPE_TRANSACTION && (
                  <ReceiptPopover receipt={details?.Receipt} />
                )}
              </div>
            </div>
          ) : (
            <div className="text-field-wrapper">
              <TextField
                dataTestId="save-clip-title"
                data-intercom-target="save-clip-title"
                changeByProp="title"
                disabled={!canCreate && !isCreator}
                errorText={errorTitle}
                labelText={`${locale.fields.title}*`}
                maxLength={MAX_LENGTH_CLIP_TITLE}
                name="title"
                onBlur={() => onBlur('title')}
                onChange={onChange}
                value={title}
              />
            </div>
          )}
          <Flex
            className={videoSettingsWrapperClass}
            alignItems="center"
            data-intercom-target="save-clip-quality"
          >
            <h3>{locale.cloudShare.videoSettings}</h3>
            <p>{locale.cloudShare.videoQualityHint}</p>
            <Flex gap="2rem" alignItems="center">
              {!isEditMode && (
                <Toggle
                  dataTestId="save-clip-quality"
                  checked={isBestQuality}
                  disabled={saveClipQualityAndMotionOnlyDisabled}
                  labelRight={highQualityToggleLabel}
                  name="best-quality"
                  onChange={this.toggleVideoQuality}
                  style={{ marginBottom: 0 }}
                />
              )}
              {displayFaceblurOption && (
                <Flex width="auto" alignItems="center" gap="0.25rem">
                  <Toggle
                    checked={faceblur}
                    labelRight={locale.faceblur.toggleFaceBlur}
                    name="best-quality"
                    onChange={this.toggleFaceblur}
                    style={{ marginBottom: 0 }}
                    disabled={isEditMode}
                  />
                  <Button
                    icon={<MDIIcon icon={InfoIcon} size={18} />}
                    type="clear"
                    className="event-timepicker-info-icon"
                    iconOnly
                    tooltip={locale.faceblur.facesBlurredInfo}
                    style={{ margin: 0 }}
                  />
                </Flex>
              )}
            </Flex>
          </Flex>
          {displayMotionOnlyVideoCheckbox && (
            <div className="text-field-wrapper">
              <Checkbox
                disabled={saveClipQualityAndMotionOnlyDisabled}
                checked={saveMotionOnlyVideo}
                style={{ marginBottom: 0 }}
                name="save-motion-only-video"
                labelText={locale.cloudShare.onlySaveVideoWithMotion}
                hintText={locale.cloudShare.saveMotionOnlyVideoExplanation}
                onChange={this.toggleMotionOnlyVideoCheckbox}
                title={locale.cloudShare.saveMotionOnlyVideo}
              />
            </div>
          )}
          {enableVisibilityEdit ? (
            <div className="text-field-wrapper">
              <RadioGroup
                changeByProp="visibility"
                checkedValue={visibility}
                hintText={locale.cloudShare.visibilityHintText}
                isInline={false}
                name="visibility"
                onChange={(prop: string, value: string | number) => onChange(prop, `${value}`)}
                title={locale.dashboard.visibility}
              >
                <Radio
                  dataTestId="save-clip"
                  labelText={locale.cloudShare.visibilityPublicLabel}
                  value={VISIBILITY_PUBLIC}
                />
                <Radio
                  dataTestId="save-clip"
                  labelText={locale.cloudShare.visibilityPrivateLabel}
                  value={VISIBILITY_PRIVATE}
                />
              </RadioGroup>
            </div>
          ) : (
            <div className="text-field-wrapper">
              <div className="details-title">{locale.dashboard.visibility}</div>
              <div className="details-content capitalize">
                {visibility ?? locale.general.unknown}
              </div>
            </div>
          )}
          <div className="text-field-wrapper" data-intercom-target="save-clip-notes">
            <TextArea
              dataTestId="save-clip-notes"
              changeByProp="description"
              labelText={locale.fields.notes}
              maxLength={MAX_LENGTH_SHARE_MESSAGE}
              name="description"
              value={description}
              onBlur={() => onBlur('description')}
              onChange={onChange}
              rows={3}
            />
          </div>
          {canShare && (
            <div
              className="text-field-wrapper"
              id="email-wrapper"
              data-intercom-target="email-wrapper"
            >
              {this.alreadyShared && (
                <div
                  className="details-title"
                  style={styles.shareDetailsTitle as React.CSSProperties}
                >
                  {locale.cloudShare.shareDetails}
                </div>
              )}
              {faceblur && ifShare && !this.alreadyShared && (
                <p className="face-blur-share-info">
                  {locale.faceblur.recipientsNotAutomaticallyNotified}
                </p>
              )}
              <div className={emailWrapperClass}>
                {!this.alreadyShared && (
                  <Checkbox
                    dataTestId="save-clip-emails"
                    checked={ifShare}
                    disabled={contextMode === CONTEXT_DOWNLOAD}
                    labelText={locale.cloudShare.shareWithSomeone}
                    name="if-share"
                    onChange={this.handleShareToggle}
                    style={{ marginBottom: 0, padding: 12, borderBottom: '1px solid #CECECE' }}
                  />
                )}
                <div className={recipientWrapperClass}>
                  <Flex className="share-panel" alignItems="center">
                    <Flex className="recipient-list" alignItems="center">
                      {shares.map((share, idx) => {
                        const isNewShare = !share.id;

                        // Don't render originator (user who initiated the share)
                        if (share.sharedBy === share.email) {
                          return <div key={idx}></div>;
                        }
                        const accesses = share.views || [];

                        const accessStatusClass = classNames({
                          seen: accesses.length > 0,
                          notified: !!share.notified,
                        });

                        const recipientClass = classNames('recipient', {
                          disabled: share.status === SHARE_STATUS_DISABLED,
                          new: isNewShare,
                        });

                        const accessStatusTooltip = getShareAccessStatusText(share, locale);

                        if (share.email.length) {
                          const shareOption = shareOptions[idx];
                          const menuItems: any = [];

                          if (shareOption && shareOption.status === SHARE_STATUS_DISABLED) {
                            const nextMenuItem = {
                              onClick: () => this.toggleShareStatus(idx),
                              text: locale.cloudShare.recipientShare,
                              type: 'action',
                            };
                            menuItems.push(nextMenuItem);
                          } else if (shareOption && shareOption.status === SHARE_STATUS_ENABLED) {
                            const nextMenuItem = {
                              onClick: () => this.toggleShareStatus(idx),
                              text: locale.cloudShare.recipientUnshare,
                              type: 'action',
                            };
                            menuItems.push(nextMenuItem);
                          }

                          return (
                            <div key={idx} className={recipientClass}>
                              <Flex alignItems="center" className="wrap share-actions-wrapper">
                                {this.alreadyShared && (
                                  <Button
                                    className={accessStatusClass}
                                    icon={<MDIIcon icon={CheckCircle} />}
                                    iconOnly={true}
                                    style={styles.recipientIconButton}
                                    tooltip={accessStatusTooltip}
                                    type="clear"
                                  />
                                )}
                                <div className="email-body">{share.email}</div>
                                {isNewShare ? (
                                  <Button
                                    icon={<MDIIcon icon={Close} />}
                                    iconOnly={true}
                                    onClick={() => {
                                      removeShare(share);
                                    }}
                                    style={styles.recipientIconButton}
                                    tooltip={locale.general.remove}
                                    type="clear"
                                  />
                                ) : (
                                  <ButtonMenu
                                    buttonIcon={<MDIIcon icon={ChevronDown} size={18} />}
                                    buttonIconOnly={true}
                                    buttonStyle={styles.recipientIconButton}
                                    buttonTooltip={locale.general.more}
                                    buttonType="clear"
                                    menuItems={menuItems}
                                  />
                                )}
                              </Flex>
                            </div>
                          );
                        }
                      })}
                      {recipientsCount > 1 && (
                        <Pill
                          key="unshare-all"
                          title={unshareAllToggleText}
                          value={unshareAllToggleText}
                          onClick={this.toggleShareAll}
                          style={{ lineHeight: 1, padding: '5px 12px', margin: '0 7px 10px 0' }}
                        />
                      )}
                      <TextField
                        refFunc={this.emailInputRef}
                        dataTestId="save-clip-emails"
                        changeByProp="emails"
                        name="emails"
                        value={emails}
                        placeholder={
                          recipientsCount > 0 ? locale.general.more : locale.cloudShare.recipients
                        }
                        style={{ width: 'auto', minWidth: '50%', height: 'auto' }}
                        inputStyle={{
                          fontSize: 13,
                          boxSizing: 'border-box',
                          padding: '5px 0',
                          border: 'none',
                          bottom: 5,
                        }}
                        disabled={!ifShare}
                        onChange={onChange}
                        onBlur={() => onBlur('emails')}
                        onKeyPress={onKeyPressEmail}
                      />
                    </Flex>
                  </Flex>
                </div>
                <Flex flexDirection="column" className={shareMessageWrapperClass}>
                  <Flex flexDirection="column">
                    <TextArea
                      dataTestId="save-clip-emails-message"
                      changeByProp="shareMessage"
                      disabled={!ifShare}
                      name="shareMessage"
                      onBlur={() => onBlur('shareMessage')}
                      onChange={onChange}
                      onFocus={onFocusShareMessage}
                      placeholder={`${locale.fields.emailMessage} (${locale.general.optional})`}
                      rows={4}
                      style={{ width: '100%' }}
                      textAreaStyle={{ fontSize: 13, border: 'none' }}
                      value={shareMessage}
                    />
                  </Flex>
                </Flex>
                {showCharCountShareMessage && (
                  <div className="char-available">
                    {LocaleUtil.getLocalizedStr({
                      string: locale.fields.charactersAvailable,
                      replacements: {
                        count: MAX_LENGTH_SHARE_MESSAGE - shareMessage.length,
                      },
                    })}
                  </div>
                )}
                <div className="email-error-message">{errorEmail}</div>
              </div>
            </div>
          )}
        </Flex>
      </div>
    );
  }

  updateStartTime = (value: Date | undefined) => {
    if (value) {
      this.props.onDateChange('start-time', value);
    }
  };

  updateEndTime = (value: Date | undefined) => {
    if (value) {
      this.props.onDateChange('end-time', value);
    }
  };

  checkSaveActiveEvent = ({ value }: { value: boolean }) => {
    const { updateSaveActiveEvent } = this.props;
    this.setState({ saveActiveEvent: value });
    updateSaveActiveEvent(value);
  };

  toggleMotionOnlyVideoCheckbox = () => {
    const { onChange, saveMotionOnlyVideo } = this.props;
    onChange('save-motion-only-video', !saveMotionOnlyVideo);
  };

  toggleVideoQuality = () => {
    const { onChange, isBestQuality, saveMotionOnlyVideo } = this.props;
    onChange('best-quality', !isBestQuality);
    if (isBestQuality && saveMotionOnlyVideo) {
      onChange('save-motion-only-video', false);
    }
  };

  toggleFillGaps = () => {
    const { onChange, fillGaps } = this.props;
    onChange('fill-gaps', !fillGaps);
  };

  localeDateFormat = (date: Date, locale: LocaleDefinition) =>
    moment(date).locale(locale.locale).format(locale.date.format2);

  toggleShareStatus = (sharesOptionIndex: number) => {
    const { event, shares, diffShares, locale, updateSharesState, updateDiffSharesState } =
      this.props;

    const isShareOptionsValidIdx = sharesOptionIndex >= 0; // Index 0 is for first shared contact

    if (event && isShareOptionsValidIdx) {
      const originalStatus = shares[sharesOptionIndex].status ?? SHARE_STATUS_ENABLED;
      const shareOption = shares[sharesOptionIndex];
      const newStatus =
        !shareOption || originalStatus === SHARE_STATUS_ENABLED
          ? SHARE_STATUS_DISABLED
          : SHARE_STATUS_ENABLED;

      const newDiffShares = clone(diffShares);
      const newShares = cloneDeep(shares);

      newShares[sharesOptionIndex].status = newStatus;

      const diffShareIdx = newDiffShares.findIndex((share) => {
        return share.id && share.id === shareOption.id;
      });

      // share's status change is being reverted, remove it from tracked changes
      if (diffShareIdx !== -1) {
        newDiffShares.splice(diffShareIdx, 1);
      } else {
        // Shares status changed (so track difference)
        // IMPORTANT: We must track specific changes
        newDiffShares.push({ id: shareOption.id, status: newStatus });
      }

      updateSharesState(newShares);
      updateDiffSharesState(newDiffShares);
    } else {
      toast.error(locale.error.shareUnshare);
    }
  };

  toggleShareAll = () => {
    const { shares, diffShares, updateSharesState, updateDiffSharesState } = this.props;
    const { unshareAll } = this.state;

    const diffIdDict: { [id: string]: Partial<Share> } = {};
    const diffEmailDict: { [email: string]: Partial<Share> } = {};
    let clonedDiffShares: Partial<Share>[] = [];

    diffShares.forEach((share) => {
      const { id, email } = share;
      const clonedShare = cloneDeep(share);

      if (id) {
        diffIdDict[id] = clonedShare;
      }

      if (email) {
        diffEmailDict[email] = clonedShare;
      }

      clonedDiffShares = [...clonedDiffShares, clonedShare];
    });

    const newDiffShares = unshareAll ? [] : clonedDiffShares;
    const newShares: Share[] = [];

    shares.forEach((share) => {
      const { id, email, sharedBy } = share;
      const isUserSelf = email === sharedBy;
      const statusDiff = diffIdDict[id as string];
      const newShare = diffEmailDict[email];
      const isNotInDiff = !statusDiff && !newShare;
      const clonedShare = cloneDeep(share);

      if (isUserSelf) {
        newShares.push(clonedShare);
      } else {
        if (unshareAll && !newShare) {
          // Unshare All - only keep existing recipients and disable them
          clonedShare.status = SHARE_STATUS_DISABLED;
          newShares.push(clonedShare);
          newDiffShares.push({
            id,
            status: SHARE_STATUS_DISABLED,
          });
        } else if (!unshareAll && (isNotInDiff || statusDiff || newShare)) {
          // Share All - enable existing recipients
          clonedShare.status = SHARE_STATUS_ENABLED;
          newShares.push(clonedShare);

          if (isNotInDiff) {
            newDiffShares.push({
              id,
              status: SHARE_STATUS_ENABLED,
            });
          } else if (statusDiff) {
            statusDiff.status = SHARE_STATUS_ENABLED;
          }
        }
      }
    });

    this.setState({
      unshareAll: !unshareAll,
    });
    updateDiffSharesState(newDiffShares);
    updateSharesState(newShares);
  };

  handleShareToggle = ({ value }: { value: boolean }) => {
    const { onChange, ifShare, updateEmailsState, updateSharesState, updateShareMessageState } =
      this.props;

    if (!ifShare) {
      // focus the email field to indicate where to input
      this.emailInputRef.current?.focus();
    } else {
      this.emailInputRef.current?.blur();

      updateEmailsState('');
      updateSharesState([]);
      updateShareMessageState('');
    }
    onChange('ifShare', value);
  };

  toggleFaceblur = () => {
    const { onChange, faceblur } = this.props;
    onChange('faceblur', !faceblur);
  };
}

const ComposedDetailsForm = (props: DetailsFormProps) => {
  const locale = useLocale();
  const userSession = useUserSession();
  return <DetailsForm {...props} locale={locale} userSession={userSession} />;
};

export default ComposedDetailsForm;
